import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api';
import type { ImportStatusResponse } from 'we-tree-api-client/v2';
import { useUIStore, useDomainStore, useFamilyStore } from '@/hooks/useStores';

/**
 * Custom hook for polling GEDCOM import status
 * Automatically polls when there's an active import job and handles completion
 *
 * @returns Current import status or null if no active import
 */
export function useImportStatusPolling(): ImportStatusResponse | null {
  const uiStore = useUIStore();
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const navigate = useNavigate();
  const pollingIntervalRef = useRef<number | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatusResponse | null>(null);

  useEffect(() => {
    // Cleanup any existing interval
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // If no active import, don't set up polling
    if (!uiStore.activeImportJobId) {
      setImportStatus(null);
      return;
    }

    const pollImportStatus = async () => {
      if (!uiStore.activeImportJobId) {
        return;
      }

      try {
        const result = await api.gedcomImports.getImportStatus(uiStore.activeImportJobId);

        if (!result.success) {
          // Handle typed error with better logging
          // eslint-disable-next-line no-console
          console.error('Failed to get import status:', result.error.getUserMessage());

          // If it's an auth error, don't clear the import (user might need to re-login)
          if (result.error.isAuthError()) {
            // eslint-disable-next-line no-console
            console.warn('Authentication error while polling import status');
            return; // Keep polling, user might re-authenticate
          }

          // For other errors, clear the active import
          uiStore.clearActiveImport();
          setImportStatus(null);
          return;
        }

        // Update the status display immediately
        setImportStatus(result.data);

        // Check if import is complete (completed, failed, cancelled, or rolled_back)
        const finalStatuses = ['completed', 'failed', 'cancelled', 'rolled_back'];
        if (finalStatuses.includes(result.data.status)) {
          // Clear active import (but keep importStatus so spinner stays visible)
          uiStore.clearActiveImport();
          await domainStore.loadFamiliesMetadata();

          // Reload family data if we have a current family loaded
          // This ensures the tree view shows the newly imported members
          if (result.data.familyId && familyStore.familyId === result.data.familyId) {
            await familyStore.loadFamilyData(result.data.familyId);
          }

          // Brief pause to show completion status before redirecting
          await new Promise((resolve) => setTimeout(resolve, 1500));
          // Navigate away - status will remain visible during transition
          // State will be cleaned up by useEffect or when component unmounts
          navigate(-1);
        }
        // Otherwise, continue polling
      } catch (error) {
        // This catch block now handles unexpected errors (network issues, etc.)
        // The wrapped API already handles and logs API errors above
        // eslint-disable-next-line no-console
        console.error('Unexpected error while polling import status:', error);
        // On unexpected error, clear the active import to stop polling
        uiStore.clearActiveImport();
        setImportStatus(null);
      }
    };

    // Poll immediately to get initial status
    if (uiStore.activeImportJobId) {
      pollImportStatus();
    }

    // Set up polling interval (every 1.2 seconds)
    pollingIntervalRef.current = window.setInterval(pollImportStatus, 1200);

    // Cleanup interval on unmount or when active import changes
    return () => {
      if (pollingIntervalRef.current) {
        window.clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [uiStore.activeImportJobId, uiStore, domainStore, familyStore, navigate]);

  return importStatus;
}
